import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Lock, Eye, EyeOff, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation.');
    }
    setIsSubmitting(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4 pt-24 pb-12">
        <Card className="w-full max-w-md p-8 shadow-lg text-center" data-testid="reset-password-invalid">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-500 text-sm mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Button onClick={() => navigate('/mot-de-passe-oublie')} className="bg-green-600 hover:bg-green-700 text-white">
            Refaire une demande
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4 pt-24 pb-12">
      <Card className="w-full max-w-md p-8 shadow-lg" data-testid="reset-password-card">
        <div className="text-center mb-6">
          <button onClick={() => navigate('/')} className="inline-flex items-center mb-4 hover:opacity-80 transition-opacity">
            <img src="/logo-ler.png" alt="Location Benne Chantier" className="h-16 w-auto object-contain" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Nouveau mot de passe</h1>
          <p className="text-gray-500 mt-2 text-sm">Choisissez votre nouveau mot de passe</p>
        </div>

        {success ? (
          <div className="text-center space-y-4" data-testid="reset-password-success">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Mot de passe modifié !</h2>
            <p className="text-gray-500 text-sm">
              Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <Button
              className="mt-4 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => navigate('/connexion')}
              data-testid="go-to-login-btn"
            >
              Se connecter
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-base font-semibold">Nouveau mot de passe</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="h-12 pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  required
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm" className="text-base font-semibold">Confirmer le mot de passe</Label>
              <div className="relative mt-2">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  className="h-12 pr-12"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retapez votre mot de passe"
                  required
                  data-testid="reset-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg" data-testid="reset-password-error">{error}</p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
              data-testid="reset-password-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Réinitialisation...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-5 w-5" />
                  Réinitialiser le mot de passe
                </>
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/connexion')}
                className="text-sm text-green-600 hover:text-green-700 font-medium hover:underline"
              >
                <ArrowLeft className="inline h-3.5 w-3.5 mr-1" />
                Retour à la connexion
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;
